/**
 * SearchableSelect. The shared dropdown the rest of Cockpit uses for any
 * filter that may show more than 15 options.
 *
 * Rule: per COCKPIT.md section 2.8, any filter dropdown whose option list
 * can exceed 15 entries renders a search input at the top of the popover.
 * Below the threshold the search input is suppressed and the popover is a
 * plain scroll list. The threshold is configurable per call site
 * (`searchThreshold`) but defaults to 15 so pages do not need to think
 * about it.
 *
 * Behavior:
 *   - Trigger button shows the count + a summary of the current selection
 *     ("Any", a single option's label, or "N selected").
 *   - Popover opens below the trigger, click outside or Escape closes it.
 *   - When options.length > searchThreshold, a search input renders at
 *     the top of the popover and autofocuses on open.
 *   - Filtering is case-insensitive substring on the option label, falling
 *     back to the option value when no label match is found. This makes
 *     enum-style values searchable by code as well as label.
 *   - Sublabels render as a smaller muted second line beneath the label.
 *     Use them for things like city + state on a partner row.
 *   - Multi-select renders checkboxes. Single-select renders radio-like
 *     rows that close the popover after the choice is made.
 *   - A Clear footer appears whenever the selection is non-empty.
 *
 * The InfoTooltip wrapping is left to callers because the help text
 * varies per filter; this component is purely the input affordance.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export type SearchableSelectOption<T extends string = string> = {
  value: T;
  label: string;
  /** Optional secondary line shown beneath the label in the option row. */
  sublabel?: string;
};

export type SearchableSelectProps<T extends string = string> = {
  /** Full list of options. Order is preserved when no search text is entered. */
  options: Array<SearchableSelectOption<T>>;
  /** Currently selected values. Pass [v] for single-select call sites. */
  selected: T[];
  /** Notified with the next selection array on every change. */
  onChange: (next: T[]) => void;
  /** When false, picking an option replaces the selection and closes. */
  multiSelect?: boolean;
  /** Text on the trigger button when nothing is selected. Defaults to "Any". */
  placeholder?: string;
  /**
   * Inclusive threshold. When `options.length > searchThreshold`, the
   * search input renders. Defaults to 15.
   */
  searchThreshold?: number;
  /** Message shown inside the popover when the search filter matches nothing. */
  emptyMessage?: string;
  /** Optional. Wraps the trigger; useful for grid layout adjustments. */
  className?: string;
  /** Optional label for screen readers; also used in the trigger aria-label. */
  ariaLabel?: string;
};

export function SearchableSelect<T extends string = string>({
  options,
  selected,
  onChange,
  multiSelect = true,
  placeholder = 'Any',
  searchThreshold = 15,
  emptyMessage = 'No matches.',
  className,
  ariaLabel,
}: SearchableSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const showSearch = options.length > searchThreshold;

  // Click outside + Escape close, only while open.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Autofocus the search input on open. Reset the query when closing so
  // the next open starts from the full list scrolled to the top.
  useEffect(() => {
    if (open && showSearch) {
      // Microtask defer so the input is mounted before we focus it.
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
    if (!open) setQuery('');
  }, [open, showSearch]);

  const filtered = useMemo(() => {
    if (!showSearch || !query.trim()) return options;
    const needle = query.trim().toLowerCase();
    return options.filter((o) => {
      const labelMatch = o.label.toLowerCase().includes(needle);
      if (labelMatch) return true;
      // Fall back to the value when the label did not match. Useful for
      // enum-style options whose value is the actual searchable token.
      return o.value.toLowerCase().includes(needle);
    });
  }, [options, query, showSearch]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const buttonLabel = useMemo(() => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) {
      const hit = options.find((o) => o.value === selected[0]);
      return hit?.label ?? selected[0];
    }
    return `${selected.length} selected`;
  }, [selected, options, placeholder]);

  const toggle = useCallback(
    (value: T) => {
      if (!multiSelect) {
        onChange([value]);
        setOpen(false);
        return;
      }
      if (selectedSet.has(value)) {
        onChange(selected.filter((v) => v !== value));
      } else {
        onChange([...selected, value]);
      }
    },
    [multiSelect, onChange, selected, selectedSet],
  );

  const clear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className="min-w-[120px] inline-flex items-center justify-between gap-2 text-left border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown
          aria-hidden
          className={`h-3 w-3 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable={multiSelect || undefined}
          className="absolute z-30 mt-1 w-72 max-w-[20rem] border border-amp-line rounded bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface"
        >
          {showSearch && (
            <div className="p-2 border-b border-amp-line dark:border-amp-dark-line">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="w-full border border-amp-line rounded px-2 py-1 text-xs bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {emptyMessage}
              </div>
            ) : (
              filtered.map((o) => {
                const checked = selectedSet.has(o.value);
                return (
                  <label
                    key={o.value}
                    className="flex items-start gap-2 px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm cursor-pointer"
                  >
                    <input
                      type={multiSelect ? 'checkbox' : 'radio'}
                      checked={checked}
                      onChange={() => toggle(o.value)}
                      className="mt-0.5"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{o.label}</span>
                      {o.sublabel && (
                        <span className="block truncate text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          {o.sublabel}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={clear}
              className="w-full text-left px-3 py-1.5 text-xs text-amp-olive dark:text-amp-olive-dark border-t border-amp-line dark:border-amp-dark-line"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
